import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Navbar from '../components/nav/Navbar';
import { useAuth } from '../context/AuthContext';
import { useFanProfileData } from '../hooks/useFanProfileData';
import { usePlayer } from '../context/PlayerContext';
import supabase from '../supabase/supabase';

const { 
  FiBox, FiPlay, FiPause, FiDownload, FiMusic, FiLoader, FiLock, FiGrid, FiList, 
  FiFolder, FiFolderPlus, FiPlus, FiX, FiCheck
} = FiIcons;

const Vault = () => {
  const { user } = useAuth();
  const { unlockedDrops, loading } = useFanProfileData(user?.id);
  const { playTrack, currentTrack, isPlaying, togglePlay } = usePlayer();
  
  const [view, setView] = useState('grid');
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  
  // Modal for adding a drop to a folder
  const [modalItem, setModalItem] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchFolders();
  }, [user]);

  const fetchFolders = async () => {
    const { data } = await supabase
      .from('d2f_vault_folders')
      .select('*, items:d2f_vault_folder_items(drop_id)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    setFolders(data || []);
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    
    await supabase.from('d2f_vault_folders').insert({
      user_id: user.id,
      name: newFolderName.trim()
    });
    
    setNewFolderName('');
    setShowNewFolder(false);
    fetchFolders();
  };

  const handleToggleFolderItem = async (folderId, dropId) => {
    const folder = folders.find(f => f.id === folderId);
    const inFolder = folder?.items.some(i => i.drop_id === dropId);
    
    if (inFolder) {
      await supabase.from('d2f_vault_folder_items').delete().match({ folder_id: folderId, drop_id: dropId });
    } else {
      await supabase.from('d2f_vault_folder_items').insert({ folder_id: folderId, drop_id: dropId });
    }
    fetchFolders();
  };

  const handlePlay = (item) => {
    const drop = item.drop;
    if (!drop) return;
    const track = {
      id: drop.id,
      title: drop.title,
      releaseContext: { cover: drop.cover_url, artist: 'Artist', album: drop.title },
    };
    if (currentTrack?.id === drop.id) { togglePlay(); return; }
    playTrack(track, { cover: drop.cover_url, artist: 'Artist', album: drop.title });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-6">
            <div className="w-20 h-20 glass-card rounded-3xl flex items-center justify-center mx-auto">
              <SafeIcon icon={FiLock} className="text-3xl text-zinc-600" />
            </div>
            <h2 className="text-3xl font-black uppercase tracking-tight">Sign In to Access Vault</h2>
            <Link to="/auth" className="inline-block px-8 py-4 btn-brand rounded-2xl text-sm">Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  // Filter items based on active folder
  let displayedItems = unlockedDrops;
  if (activeFolderId) {
    const activeFolder = folders.find(f => f.id === activeFolderId);
    if (activeFolder) {
      const dropIdsInFolder = new Set(activeFolder.items.map(i => i.drop_id));
      displayedItems = unlockedDrops.filter(item => dropIdsInFolder.has(item.drop?.id));
    }
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[500px] h-[500px] top-[-10%] right-[-10%] animate-caustic" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24 space-y-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Your Collection</span>
            <h1 className="text-5xl font-black uppercase tracking-tighter text-white mt-1">My Vault</h1>
            <p className="text-zinc-500 font-bold text-sm mt-2">
              {loading ? 'Loading…' : `${unlockedDrops.length} unlocked drop${unlockedDrops.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex gap-1 glass-card rounded-xl p-1 self-start md:self-auto">
            {[{ m: 'grid', i: FiGrid }, { m: 'list', i: FiList }].map(({ m, i }) => (
              <button key={m} onClick={() => setView(m)}
                className={`p-2.5 rounded-lg transition-all ${view === m ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'}`}>
                <SafeIcon icon={i} className="text-sm" />
              </button>
            ))}
          </div>
        </div>

        {/* Folders Navigation */}
        <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
          <button 
            onClick={() => setActiveFolderId(null)}
            className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${
              activeFolderId === null 
                ? 'bg-white text-black' 
                : 'glass-card text-zinc-400 hover:text-white'
            }`}
          >
            All Drops
          </button>
          
          {folders.map(folder => (
            <button 
              key={folder.id}
              onClick={() => setActiveFolderId(folder.id)}
              className={`px-5 py-2.5 flex items-center gap-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                activeFolderId === folder.id 
                  ? 'bg-zinc-800 text-white border border-zinc-700' 
                  : 'glass-card text-zinc-400 hover:text-white'
              }`}
            >
              <SafeIcon icon={FiFolder} className="text-xs" />
              {folder.name}
              <span className="ml-1 opacity-50 block text-[9px] bg-white/10 px-1.5 rounded">{folder.items?.length || 0}</span>
            </button>
          ))}
          
          {showNewFolder ? (
            <form onSubmit={handleCreateFolder} className="flex items-center gap-2 glass-card rounded-xl p-1 pr-2">
              <input 
                type="text" 
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Folder Name..."
                className="bg-transparent border-none outline-none text-xs font-bold px-3 w-32 text-white placeholder-zinc-600 focus:ring-0"
                autoFocus
              />
              <button type="submit" className="w-6 h-6 bg-brand-pink text-white rounded flex items-center justify-center hover:bg-brand-coral transition-colors">
                <SafeIcon icon={FiCheck} />
              </button>
              <button type="button" onClick={() => setShowNewFolder(false)} className="w-6 h-6 glass-subtle text-zinc-400 rounded flex items-center justify-center hover:text-white transition-colors">
                <SafeIcon icon={FiX} />
              </button>
            </form>
          ) : (
            <button 
              onClick={() => setShowNewFolder(true)}
              className="px-4 py-2.5 flex items-center gap-2 rounded-xl text-xs font-black uppercase tracking-widest glass-card text-brand-pink border-brand-pink/20 hover:bg-brand-pink/10 transition-all whitespace-nowrap"
            >
              <SafeIcon icon={FiPlus} className="text-sm" />
              New Folder
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <SafeIcon icon={FiLoader} className="text-3xl text-brand-pink animate-spin" />
          </div>
        ) : displayedItems.length === 0 ? (
          <div className="flex flex-col items-center gap-6 py-24 text-center">
            <div className="w-20 h-20 glass-card rounded-3xl flex items-center justify-center">
              <SafeIcon icon={activeFolderId ? FiFolder : FiBox} className="text-3xl text-zinc-700" />
            </div>
            <div>
              <p className="text-white font-black text-xl uppercase tracking-tight">
                {activeFolderId ? 'Folder is Empty' : 'Vault is Empty'}
              </p>
              <p className="text-zinc-600 font-bold text-sm mt-1">
                {activeFolderId 
                  ? 'Add drops to this folder to organize your collection.' 
                  : 'Unlock drops from your favourite artists to build your library.'}
              </p>
            </div>
            {!activeFolderId && (
              <Link to="/discover" className="px-6 py-3 btn-brand rounded-2xl text-xs">Browse Drops</Link>
            )}
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {displayedItems.map(item => {
              const playing = currentTrack?.id === item.drop?.id && isPlaying;
              return (
                <div key={item.id} className="relative group glass-card rounded-3xl overflow-hidden hover:-translate-y-1 transition-all duration-300">
                  <div className="aspect-square relative overflow-hidden bg-zinc-900">
                    {item.drop?.cover_url
                      ? <img src={item.drop.cover_url} alt={item.drop.title}
                          className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                      : <div className="w-full h-full flex items-center justify-center">
                          <SafeIcon icon={FiMusic} className="text-4xl text-zinc-700" />
                        </div>
                    }
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3">
                      <button onClick={() => handlePlay(item)}
                        className="w-12 h-12 bg-white text-black rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-2xl">
                        <SafeIcon icon={playing ? FiPause : FiPlay} className="text-lg" />
                      </button>
                      <button className="w-12 h-12 glass-strong text-white rounded-full flex items-center justify-center hover:scale-110 transition-transform">
                        <SafeIcon icon={FiDownload} className="text-base" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4 space-y-1 flex justify-between items-start">
                    <div className="min-w-0 pr-2">
                      <h3 className="font-black text-sm uppercase tracking-tight text-white truncate">{item.drop?.title || 'Untitled'}</h3>
                      <p className="text-zinc-600 text-[10px] font-black tracking-widest uppercase">{item.drop?.genre || 'Music'}</p>
                    </div>
                    <button 
                      onClick={() => setModalItem(item)}
                      className="w-8 h-8 rounded-full glass-card flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/30 transition-all flex-shrink-0"
                    >
                      <SafeIcon icon={FiFolderPlus} className="text-sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {displayedItems.map((item, i) => {
              const playing = currentTrack?.id === item.drop?.id && isPlaying;
              return (
                <div key={item.id} className="flex items-center gap-5 p-4 glass-card rounded-2xl hover:border-brand-pink/20 transition-all group">
                  <span className="text-zinc-700 font-mono text-sm w-5 text-right flex-shrink-0">{i + 1}</span>
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-zinc-900">
                    {item.drop?.cover_url
                      ? <img src={item.drop.cover_url} alt={item.drop.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center"><SafeIcon icon={FiMusic} className="text-zinc-700 text-sm" /></div>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm uppercase tracking-tight text-white truncate">{item.drop?.title || 'Untitled'}</p>
                    <p className="text-zinc-600 text-[10px] font-black tracking-widest uppercase mt-0.5">{item.drop?.genre || 'Music'}</p>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setModalItem(item)}
                      className="w-9 h-9 glass-card rounded-xl flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                      <SafeIcon icon={FiFolderPlus} className="text-sm" />
                    </button>
                    <button onClick={() => handlePlay(item)}
                      className="w-9 h-9 glass-card rounded-xl flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                      <SafeIcon icon={playing ? FiPause : FiPlay} className="text-sm" />
                    </button>
                    <button className="w-9 h-9 glass-card rounded-xl flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                      <SafeIcon icon={FiDownload} className="text-sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add to Folder Modal */}
      {modalItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-sm glass-card rounded-3xl p-6 space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="font-black text-lg uppercase tracking-tight">Add to Folder</h3>
              <button onClick={() => setModalItem(null)} className="text-zinc-500 hover:text-white">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-white/5 rounded-2xl">
              <img src={modalItem.drop?.cover_url} className="w-12 h-12 rounded-xl object-cover" alt="" />
              <div>
                <p className="font-black text-sm uppercase">{modalItem.drop?.title}</p>
                <p className="text-xs font-bold text-zinc-500">{modalItem.drop?.artist?.name}</p>
              </div>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto pr-2 scrollbar-hide">
              {folders.length === 0 ? (
                <p className="text-center text-sm font-bold text-zinc-500 py-4">No folders created yet.</p>
              ) : (
                folders.map(folder => {
                  const inFolder = folder.items.some(i => i.drop_id === modalItem.drop?.id);
                  return (
                    <button 
                      key={folder.id}
                      onClick={() => handleToggleFolderItem(folder.id, modalItem.drop?.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        inFolder 
                          ? 'bg-brand-pink/10 border-brand-pink/30 text-white' 
                          : 'glass-card text-zinc-400 hover:text-white hover:border-zinc-500'
                      }`}
                    >
                      <span className="font-black text-xs uppercase tracking-widest flex items-center gap-2">
                        <SafeIcon icon={FiFolder} /> {folder.name}
                      </span>
                      {inFolder && <SafeIcon icon={FiCheck} className="text-brand-pink" />}
                    </button>
                  );
                })
              )}
            </div>
            
            <button onClick={() => setModalItem(null)} className="w-full py-3 btn-brand rounded-xl text-xs">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vault;